/**
 * Copyright ©2024 Drivepoint
 */

import {useLocation} from "react-router-dom";
import React, {forwardRef, ReactNode, useImperativeHandle, useRef, useState} from "react";
import {ArrowDropDown, BlurOn, FiberManualRecord, HelpOutlineRounded, Menu, NoteAdd, Science, Settings} from "@mui/icons-material";
import {AppBar, CircularProgress, Container, IconButton, ListItemIcon, ListItemText, Menu as MuiMenu, MenuItem as MuiMenuItem, Toolbar, Tooltip, Typography} from "@mui/material";
import {EventBus, useStateChange} from "@bainbridge-growth/node-frontend";
import Pages from "../../services/Pages";
import BackgroundOperationsIndicator from "../BackgroundOperationsIndicator/BackgroundOperationsIndicator";
import type {ExperimentInfo} from "../ChatTestExperiment/ChatTestExperiment.tsx";
import type {DPDrawerInterface} from "../DPDrawer/DPDrawer";
import DPNavigationMenu from "../DPNavigationMenu/DPNavigationMenu.tsx";
import GenAIProviderIcon from "../GenAIProviderIcon/GenAIProviderIcon.tsx";
import "./Header.css";

export type HeaderInterface = {
  updateTitle: (title: string) => void;
  updateAction: (element: ReactNode) => void
  disableMenuButton: (value: boolean) => void
};

type HeaderProps = {};

function HeaderComponent(_, ref): any {

  const location = useLocation();

  const drawer = useRef<DPDrawerInterface>();

  const [title, setTitle] = useState<string | null>(null);
  const [actionElement, setActionElement] = useState<ReactNode | null>(null);
  const [disableMenuButton, setDisableMenuButton] = useState<boolean>(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [modelAnchorEl, setModelAnchorEl] = useState<null | HTMLElement>(null);

  useImperativeHandle(ref, (): HeaderInterface => ({
    updateTitle: (title: string) => { setTitle(title); },
    updateAction: (element: ReactNode) => { setActionElement(element); },
    disableMenuButton: (value: boolean) => { setDisableMenuButton(value); }
  }));

  function onPagesButtonClick(): void {
    drawer.current.toggle();
  }

  const flags = useStateChange<any>("flags");
  const user = useStateChange<any>("user");
  const isAnonymous = !user;

  const homechatExperimentInfo = useStateChange<ExperimentInfo>("homechat:experimentInfo");
  const homechatRecordingState = useStateChange<string>("homechat:recordingState") ?? "idle";
  const homechatModel = useStateChange<{id: string; displayName: string; provider: string}>("homechat:model");
  const availableModels = useStateChange<any[]>("homechat:availableModels");
  const selectedModel = useStateChange<{provider: string; model: string}>("homechat:selectedModel");

  function onChatClick(): void {
    EventBus.dispatch({type: "show:page", id: "/HomePage"} as any);
    EventBus.dispatch({type: "homechat:showChat"} as any);
  }

  function onInfoClick(): void {
    window.open("https://docs.drivepoint.io/help", "_blank");
  }

  function getCurrentPageWithDefault(): any {
    if (location.pathname === "/") { return Pages.default; }
    return Pages.getPageByPath(location.pathname);
  }

  function isOnHomePage(): boolean {
    return location.pathname === "/" || location.pathname === "/HomePage";
  }

  function handleSettingsOpen(event: React.MouseEvent<HTMLElement>): void {
    setSettingsAnchorEl(event.currentTarget);
  }

  function handleSettingsClose(): void {
    setSettingsAnchorEl(null);
  }

  function handleNewChat(): void {
    EventBus.dispatch({type: "homechat:newChat"});
    handleSettingsClose();
  }

  function handleToggleExperiment(): void {
    EventBus.dispatch({type: "homechat:toggleExperiment"});
    handleSettingsClose();
  }

  function handleToggleRecord(): void {
    EventBus.dispatch({type: "homechat:toggleRecord"});
    handleSettingsClose();
  }

  function renderDrivepointAgentAssist(): any {
    if (!flags?.["addin.agentAssist.enabled"]) { return null; }
    if (isOnHomePage()) { return null; }
    return <Tooltip title="Drivepoint Chat">
      <IconButton className="header-toolbar-chat-button" onClick={onChatClick}>
        <BlurOn />
      </IconButton>
    </Tooltip>;
  }

  function renderQuestionButton(): any {
    return <Tooltip title="Help">
      <IconButton className="header-toolbar-info-button" onClick={onInfoClick} aria-label="Help">
        <HelpOutlineRounded />
      </IconButton>
    </Tooltip>;
  }

  function handleModelDropdownOpen(event: React.MouseEvent<HTMLElement>): void {
    setModelAnchorEl(event.currentTarget);
  }

  function handleModelDropdownClose(): void {
    setModelAnchorEl(null);
  }

  function handleModelSelect(provider: string, model: string): void {
    EventBus.dispatch({type: "homechat:selectModel", provider, model} as any);
    handleModelDropdownClose();
  }

  function renderModelDropdownItems(): ReactNode[] {
    if (!availableModels?.length) { return []; }
    const items: ReactNode[] = [];
    for (const provider of availableModels) {
      for (const model of provider.models) {
        const isSelected = selectedModel?.provider === provider.name && selectedModel?.model === model.id;
        items.push(
          <MuiMenuItem
            dense
            className="header-model-dropdown-item"
            key={`${provider.name}-${model.id}`}
            selected={isSelected}
            onClick={() => handleModelSelect(provider.name, model.id)}
          >
            <ListItemIcon sx={{minWidth: "22px!important", fontSize: "0.8rem"}}><GenAIProviderIcon provider={provider.name} /></ListItemIcon>
            <ListItemText className="header-model-dropdown-item-text">{model.displayName}</ListItemText>
          </MuiMenuItem>
        );
      }
    }
    return items;
  }

  function renderHomePageControls(): ReactNode {
    const provider = homechatModel?.provider ?? "anthropic";
    const displayName = homechatModel?.displayName ?? "Sonnet 4.5";

    const isRecording = homechatRecordingState === "recording" || homechatRecordingState === "completing";

    return <>
      <div className="header-toolbar-spacer" />
      <div className="header-toolbar-model-selector" onClick={handleModelDropdownOpen}>
        <GenAIProviderIcon provider={provider} />
        <Typography className="header-toolbar-model-name">{displayName}</Typography>
        <ArrowDropDown className="header-toolbar-model-dropdown" />
      </div>
      <MuiMenu 
        className="header-model-dropdown-menu"
        anchorEl={modelAnchorEl} 
        open={Boolean(modelAnchorEl)} 
        onClose={handleModelDropdownClose}>
        {renderModelDropdownItems()}
      </MuiMenu>
      <Tooltip title="Settings">
        <IconButton className="header-toolbar-settings-button" size="small" onClick={handleSettingsOpen}>
          <Settings fontSize="small" />
        </IconButton>
      </Tooltip>
      <MuiMenu anchorEl={settingsAnchorEl} open={Boolean(settingsAnchorEl)} onClose={handleSettingsClose}>
        <MuiMenuItem onClick={handleNewChat}>
          <ListItemIcon><NoteAdd fontSize="small" /></ListItemIcon>
          <ListItemText>New Chat</ListItemText>
        </MuiMenuItem>
        {user?.excelUser?.role === "superAdmin" && (
          <MuiMenuItem onClick={handleToggleRecord} className={isRecording ? "header-menu-item-recording" : ""}>
            <ListItemIcon>
              <FiberManualRecord fontSize="small" className={isRecording ? "header-menu-icon-recording" : ""} />
            </ListItemIcon>
            <ListItemText>{isRecording ? "Finish Test" : "Record Test"}</ListItemText>
          </MuiMenuItem>
        )}
        {user?.excelUser?.role === "superAdmin" && (
          <MuiMenuItem onClick={handleToggleExperiment} disabled={homechatExperimentInfo?.running}>
            <ListItemIcon>
              {homechatExperimentInfo?.running ? <CircularProgress size={18} /> : <Science fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{homechatExperimentInfo?.running ? "Running..." : "Build Experiment"}</ListItemText>
          </MuiMenuItem>
        )}
      </MuiMenu>
    </>;
  }

  const showHomeControls = isOnHomePage() && !isAnonymous && flags?.["addin.agentAssist.enabled"];

  return <div className="header">
    <AppBar className="header-app-bar" position="static">
      <Container className="header-container" maxWidth="xl">
        <Toolbar className="header-toolbar" disableGutters>
          {!isAnonymous && (actionElement ?? <IconButton disabled={disableMenuButton} className="header-toolbar-pages-button" onClick={onPagesButtonClick} aria-label="Menu">
            <Menu className="header-toolbar-pages-icon"/>
          </IconButton>)}
          <div className="header-toolbar-page-name">{title ?? getCurrentPageWithDefault()?.name}</div>
          {showHomeControls && renderHomePageControls()}
          {!showHomeControls && <>
            <div className="header-toolbar-spacer" />
            <BackgroundOperationsIndicator />
            {renderDrivepointAgentAssist()}
            {isAnonymous && <button className="header-sign-in-button" onClick={() => EventBus.dispatch({type: "show:page", id: "/SignInPage"} as any)}>Sign In</button>}
            {renderQuestionButton()}
          </>}
        </Toolbar>
      </Container>
    </AppBar>
    <DPNavigationMenu ref={drawer} />
  </div>;

}

const Header = forwardRef<HeaderInterface, HeaderProps>(HeaderComponent);
export default Header;
